import { useState, useEffect, useCallback } from 'react';
import { billingApi } from '../../../shared/services/api/billingApi';
import { toast } from '../../../shared/services/toast';

export function usePaymentMethods() {
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchPaymentMethods = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await billingApi.getPaymentMethods();
      if (response.success) {
        setPaymentMethods(response.data);
      } else {
        setError(response.message || 'Failed to fetch payment methods.');
      }
    } catch (err) {
      console.error('Error fetching payment methods:', err);
      setError('An unexpected error occurred while fetching payment methods.');
    } finally {
      setLoading(false);
    }
  }, []);

  const addPaymentMethod = async (payload) => {
    try {
      const response = await billingApi.savePaymentMethod(payload);
      if (response.success) {
        toast.success('Payment method added successfully.');
        await fetchPaymentMethods();
        return true;
      } else {
        toast.error(response.message || 'Failed to add payment method.');
        return false;
      }
    } catch (err) {
      console.error('Error adding payment method:', err);
      toast.error('An unexpected error occurred.');
      return false;
    }
  };

  const deletePaymentMethod = async (token) => {
    try {
      const response = await billingApi.deletePaymentMethod(token);
      if (response.success) {
        toast.success('Payment method deleted successfully.');
        await fetchPaymentMethods();
        return true;
      } else {
        toast.error(response.message || 'Failed to delete payment method.');
        return false;
      }
    } catch (err) {
      console.error('Error deleting payment method:', err);
      toast.error('An unexpected error occurred.');
      return false;
    }
  };

  const setDefaultPaymentMethod = async (token) => {
    try {
      const response = await billingApi.setDefaultPaymentMethod(token);
      if (response.success) {
        toast.success('Default payment method updated.');
        await fetchPaymentMethods();
        return true;
      } else {
        toast.error(response.message || 'Failed to update default payment method.');
        return false;
      }
    } catch (err) {
      console.error('Error updating default payment method:', err);
      toast.error('An unexpected error occurred.');
      return false;
    }
  };

  const verifyAch = async (payload) => {
    try {
      const response = await billingApi.verifyAch(payload);
      if (response.success) {
        toast.success('ACH account verified successfully.');
        await fetchPaymentMethods();
        return true;
      } else {
        toast.error(response.message || 'Verification failed.');
        return false;
      }
    } catch (err) {
      console.error('Error verifying ACH:', err);
      toast.error('An unexpected error occurred.');
      return false;
    }
  };

  useEffect(() => {
    fetchPaymentMethods();
  }, [fetchPaymentMethods]);

  return {
    paymentMethods,
    loading,
    error,
    refetch: fetchPaymentMethods,
    addPaymentMethod,
    deletePaymentMethod,
    setDefaultPaymentMethod,
    verifyAch,
  };
}
